/**
 * Global Pincode Manager
 * Handles pincode checking across the entire site
 * - Shows popup on first visit
 * - Stores pincode in localStorage
 * - Provides global pincode state
 * - Handles time-based eligibility
 */

class GlobalPincodeManager {
  constructor() {
    this.STORAGE_KEY = 'userPincode';
    this.SKIP_KEY = 'pincodeSkipped';
    this.SKIP_TIMESTAMP_KEY = 'pincodeSkipTimestamp';
    this.POPUP_DELAY_MS = 120000;
    this.POPUP_START_HOUR_IST = 8;
    this.POPUP_END_HOUR_IST = 19;
    this.pincodeData = null;
    this.config = null;
    this.currentPincode = null;
    this.deliveryEligibility = null;
    this.popupTimer = null;
    this.debugLogs = []; // Store debug messages

    this.init();
  }

  init() {
    if ( document.readyState === 'loading' ) {
      document.addEventListener( 'DOMContentLoaded', () => this.setup() );
    } else {
      this.setup();
    }
  }

  setup() {
    // Load pincode data
    this.loadPincodeData();

    // Load configuration
    this.loadConfig();

    // Check for existing pincode
    this.checkExistingPincode();

    // Setup popup event listeners
    this.setupPopupListeners();

    // Setup trigger buttons
    this.setupTriggerButtons();

    // Update UI based on current state
    this.updateGlobalUI();

    // Create debug overlay
    // this.createDebugOverlay();
  }

  createDebugOverlay() {
    // Create overlay container
    const overlay = document.createElement('div');
    overlay.id = 'pincode-debug-overlay';
    overlay.style.cssText = `
      position: fixed;
      top: 10px;
      right: 10px;
      background: rgba(0, 0, 0, 0.9);
      color: #00ff00;
      font-family: monospace;
      font-size: 12px;
      padding: 10px;
      border-radius: 5px;
      max-width: 400px;
      max-height: 300px;
      overflow-y: auto;
      z-index: 10000;
      pointer-events: none;
      opacity: 0.8;
    `;

    // Create header
    const header = document.createElement('div');
    header.textContent = 'Pincode Debug Info';
    header.style.cssText = `
      font-weight: bold;
      margin-bottom: 5px;
      border-bottom: 1px solid #00ff00;
      padding-bottom: 3px;
    `;

    // Create content area
    const content = document.createElement('div');
    content.id = 'pincode-debug-content';
    content.style.cssText = `
      white-space: pre-wrap;
      line-height: 1.3;
    `;

    overlay.appendChild(header);
    overlay.appendChild(content);
    document.body.appendChild(overlay);

    // Update debug display every second
    setInterval(() => this.updateDebugOverlay(), 1000);
  }

  addDebugLog(message) {
    const timestamp = new Date().toLocaleTimeString();
    this.debugLogs.unshift(`[${timestamp}] ${message}`);
    
    // Keep only last 10 messages
    if (this.debugLogs.length > 10) {
      this.debugLogs = this.debugLogs.slice(0, 10);
    }
    
    this.updateDebugOverlay();
  }

  updateDebugOverlay() {
    const content = document.getElementById('pincode-debug-content');
    if (content) {
      content.textContent = this.debugLogs.join('\n');
    }
  }

  loadPincodeData() {
    if ( window.globalPincodeData ) {
      this.pincodeData = window.globalPincodeData;
    }
  }

  loadConfig() {
    const dataContainer = document.getElementById( 'globalPincodeData' );
    if ( !dataContainer ) return;

    this.config = {
      metaobjectId: dataContainer.dataset.metaobjectId,
      enableTimeBased: dataContainer.dataset.enableTimeBased === 'true',
      deliveryStartHour: parseInt( dataContainer.dataset.deliveryStartHour ) || 8,
      deliveryEndHour: parseInt( dataContainer.dataset.deliveryEndHour ) || 19,
      morningSlotEnd: parseInt( dataContainer.dataset.morningSlotEnd ) || 10,
      weekdayMorningEdd: dataContainer.dataset.weekdayMorningEdd,
      weekdayDayEdd: dataContainer.dataset.weekdayDayEdd,
      enableWeekends: dataContainer.dataset.enableWeekends === 'true',
      offDays: this.parseOffDays( dataContainer.dataset.offDays ),
      popupDelayMs: parseInt( dataContainer.dataset.popupDelayMs ) || 120000,
      saturdayStartHour: parseInt( dataContainer.dataset.saturdayStartHour ) || 0,
      saturdayMorningEndHour: parseInt( dataContainer.dataset.saturdayMorningEndHour ) || 0,
      saturdayEndHour: parseInt( dataContainer.dataset.saturdayEndHour ) || 0,
      sundayStartHour: parseInt( dataContainer.dataset.sundayStartHour ) || 0,
      sundayMorningEndHour: parseInt( dataContainer.dataset.sundayMorningEndHour ) || 0,
      sundayEndHour: parseInt( dataContainer.dataset.sundayEndHour ) || 0,
      popupTitle: dataContainer.dataset.popupTitle,
      popupDescription: dataContainer.dataset.popupDescription,
      popupButtonText: dataContainer.dataset.popupButtonText,
      popupSkipText: dataContainer.dataset.popupSkipText,
      responseInstantTitle: dataContainer.dataset.responseInstantTitle,
      responseInstantMessage: dataContainer.dataset.responseInstantMessage,
      responseTimeRestrictedTitle: dataContainer.dataset.responseTimeRestrictedTitle,
      responseTimeRestrictedMessage: dataContainer.dataset.responseTimeRestrictedMessage,
      responseOutOfAreaTitle: dataContainer.dataset.responseOutOfAreaTitle,
      responseOutOfAreaMessage: dataContainer.dataset.responseOutOfAreaMessage,
      responseContinueButton: dataContainer.dataset.responseContinueButton,
      responseChangeButton: dataContainer.dataset.responseChangeButton
    };
  }

  parseOffDays( offDaysData ) {
    if ( !offDaysData || offDaysData === 'null' || offDaysData === 'undefined' ) {
      return [];
    }
    try {
      const parsed = JSON.parse( offDaysData );
      return Array.isArray( parsed ) ? parsed : [];
    } catch ( e ) {
      console.error( 'Error parsing off days:', e );
      return [];
    }
  }

  replacePincodePlaceholder( text, pincode ) {
    if ( !text ) return text;
    return text.replace( /\[pincode\]/gi, pincode );
  }

  isHomePage() {
    return window.location.pathname === '/' || window.location.pathname === '/index' || window.location.pathname === '/index.html';
  }

  isPopupTimeAllowed() {
    const now = new Date();
    const istOffset = 5.5 * 60;
    const utcTime = now.getTime() + ( now.getTimezoneOffset() * 60000 );
    const istTime = new Date( utcTime + ( istOffset * 60000 ) );
    const istHour = istTime.getHours();
    const dayOfWeek = istTime.getDay(); // 0 = Sunday, 6 = Saturday

    let startHour, endHour;

    // Set popup hours based on day of week (reuse delivery hours)
    if ( dayOfWeek === 0 ) { // Sunday
      startHour = this.config?.sundayStartHour ?? 8;
      endHour = this.config?.sundayEndHour ?? 19;
    } else if ( dayOfWeek === 6 ) { // Saturday
      startHour = this.config?.saturdayStartHour ?? 8;
      endHour = this.config?.saturdayEndHour ?? 19;
    } else { // Monday-Friday (weekdays)
      startHour = this.config?.deliveryStartHour ?? 8;
      endHour = this.config?.deliveryEndHour ?? 19;
    }

    return istHour >= startHour && istHour < endHour;
  }

  schedulePopup() {
    if ( !this.isPopupTimeAllowed() ) {
      console.log( 'Popup not shown - outside allowed hours' );
      return;
    }
    if ( this.popupTimer ) {
      clearTimeout( this.popupTimer );
    }
    if ( this.isHomePage() ) {
      this.showPopup();
    } else {
      this.popupTimer = setTimeout( () => {
        if ( this.isPopupTimeAllowed() ) {
          this.showPopup();
        }
      }, this.config?.popupDelayMs ?? 120000 );
    }
  }

  handleSkippedPopup() {
    const skipTimestamp = sessionStorage.getItem( this.SKIP_TIMESTAMP_KEY );
    if ( !skipTimestamp ) {
      return;
    }
    if ( !this.isPopupTimeAllowed() ) {
      return;
    }
    const skipTime = parseInt( skipTimestamp );
    const now = Date.now();
    const timeSinceSkip = now - skipTime;
    const popupDelay = this.config?.popupDelayMs ?? 120000;
    if ( timeSinceSkip >= popupDelay ) {
      if ( this.popupTimer ) {
        clearTimeout( this.popupTimer );
      }
      this.popupTimer = setTimeout( () => {
        if ( this.isPopupTimeAllowed() ) {
          this.showPopup();
        }
      }, popupDelay );
    }
  }

  checkExistingPincode() {
    const stored = localStorage.getItem( this.STORAGE_KEY );
    const skipped = sessionStorage.getItem( this.SKIP_KEY );

    if ( stored ) {
      try {
        const data = JSON.parse( stored );
        this.currentPincode = data.pincode;
        this.checkDeliveryEligibility();
      } catch ( e ) {
        console.error( 'Error parsing stored pincode:', e );
      }
    } else if ( !skipped ) {
      this.schedulePopup();
    } else {
      this.handleSkippedPopup();
    }
  }

  checkDeliveryEligibility() {
    if ( !this.currentPincode || !this.pincodeData ) {
      this.deliveryEligibility = null;
      return;
    }

    const isPincodeInMetaobject = this.pincodeData[ this.currentPincode ];
    const timeCheck = this.config?.enableTimeBased ? this.checkTimeEligibility( this.currentPincode ) : { eligible: true, edd: null, message: null };

    this.deliveryEligibility = {
      pincode: this.currentPincode,
      isPincodeEligible: !!isPincodeInMetaobject,
      isTimeEligible: timeCheck.eligible,
      location: isPincodeInMetaobject ? this.pincodeData[ this.currentPincode ].location : null,
      warehouse: isPincodeInMetaobject ? this.pincodeData[ this.currentPincode ].warehouse : null,
      edd: timeCheck.edd,
      timeMessage: timeCheck.message
    };

    // Dispatch event for other components to listen
    window.dispatchEvent( new CustomEvent( 'pincodeUpdated', {
      detail: this.deliveryEligibility
    } ) );
  }

  buildTimeRestrictedMessage( pincode ) {
    return this.replacePincodePlaceholder( this.config?.responseTimeRestrictedMessage || '', pincode || '' );
  }

  checkTimeEligibility( pincode ) {
    if ( !this.config ) return { eligible: true, edd: null, message: null };

    const now = new Date();
    const day = now.getDay();

    // Convert current time to IST for delivery checks (consistent with popup timing)
    const istOffset = 5.5 * 60; // IST is UTC+5:30
    const utcTime = now.getTime() + ( now.getTimezoneOffset() * 60000 );
    const istTime = new Date( utcTime + ( istOffset * 60000 ) );
    const hour = istTime.getHours();
    const minute = istTime.getMinutes();
    const currentTime = hour + ( minute / 60 );

    this.addDebugLog(`checkTimeEligibility: {day: ${day}, currentTime: ${currentTime.toFixed(1)}, istHour: ${istTime.getHours()}, istMinute: ${istTime.getMinutes()}, pincode: '${pincode}'}`);

    // Holiday check (off days)
    if ( this.config.offDays && this.config.offDays.length > 0 ) {
      const todayStr = this.formatDate( now );
      if ( this.config.offDays.includes( todayStr ) ) {
        this.addDebugLog('Holiday check: ineligible');
        return {
          eligible: false,
          message: this.buildTimeRestrictedMessage( pincode ),
          edd: this.config.weekdayDayEdd
        };
      }
    }

    // Determine day-specific settings
    let startHour, endHour, morningEnd, morningEdd, dayEdd;

    if ( day === 0 ) { // Sunday
      startHour = this.config.sundayStartHour;
      endHour = this.config.sundayEndHour;
      morningEnd = this.config.sundayMorningEndHour;
      this.addDebugLog(`Sunday settings: {startHour: ${startHour}, endHour: ${endHour}, morningEnd: ${morningEnd}}`);
    } else if ( day === 6 ) { // Saturday
      startHour = this.config.saturdayStartHour;
      endHour = this.config.saturdayEndHour;
      morningEnd = this.config.saturdayMorningEndHour;
      this.addDebugLog(`Saturday settings: {startHour: ${startHour}, endHour: ${endHour}, morningEnd: ${morningEnd}}`);
    } else { // Weekdays (Monday-Friday)
      startHour = this.config.deliveryStartHour;
      endHour = this.config.deliveryEndHour;
      morningEnd = this.config.morningSlotEnd;
      this.addDebugLog(`Weekday settings: {startHour: ${startHour}, endHour: ${endHour}, morningEnd: ${morningEnd}}`);
    }

    // edd messages source remain same.
    morningEdd = this.config.weekdayMorningEdd;
    dayEdd = this.config.weekdayDayEdd;

    // Weekend availability check (if not enabled, disable on weekends)
    if ( !this.config.enableWeekends && ( day === 0 || day === 6 ) ) {
      this.addDebugLog('Weekend disabled: ineligible');
      return {
        eligible: false,
        message: this.buildTimeRestrictedMessage( pincode ),
        edd: dayEdd
      };
    }

    // Time checks (using IST - consistent with popup timing)
    this.addDebugLog(`Time check: {currentTime: ${currentTime.toFixed(1)}, startHour: ${startHour}, endHour: ${endHour}}`);
    if ( currentTime < startHour || currentTime >= endHour ) {
      this.addDebugLog('Time ineligible');
      const result = {
        eligible: false,
        message: this.buildTimeRestrictedMessage( pincode ),
        edd: dayEdd
      };
      this.addDebugLog(`Returning: {eligible: ${result.eligible}, message: '${result.message}', edd: '${result.edd}'}`);
      return result;
    }

    if ( currentTime >= startHour && currentTime < morningEnd ) {
      this.addDebugLog('Morning slot: eligible');
      return {
        eligible: true,
        message: null,
        edd: morningEdd
      };
    }

    if ( currentTime >= morningEnd && currentTime < endHour ) {
      this.addDebugLog('Afternoon slot: eligible');
      return {
        eligible: true,
        message: null,
        edd: dayEdd
      };
    }

    this.addDebugLog('Default: ineligible');
    return {
      eligible: false,
      message: this.buildTimeRestrictedMessage( pincode ),
      edd: dayEdd
    };
  }

  formatDate( date ) {
    const year = date.getFullYear();
    const month = String( date.getMonth() + 1 ).padStart( 2, '0' );
    const day = String( date.getDate() ).padStart( 2, '0' );
    return `${ year }-${ month }-${ day }`;
  }

  setupPopupListeners() {
    const popup = document.getElementById( 'globalPincodePopup' );
    const closeBtn = document.getElementById( 'closePincodePopup' );
    const skipBtn = document.getElementById( 'skipPincodeCheck' );
    const submitBtn = document.getElementById( 'globalPincodeSubmit' );
    const input = document.getElementById( 'globalPincodeInput' );

    if ( !popup ) return;

    // Close button
    if ( closeBtn ) {
      closeBtn.addEventListener( 'click', () => this.hidePopup() );
    }

    // Skip button
    if ( skipBtn ) {
      skipBtn.addEventListener( 'click', () => {
        sessionStorage.setItem( this.SKIP_KEY, 'true' );
        sessionStorage.setItem( this.SKIP_TIMESTAMP_KEY, Date.now().toString() );
        this.hidePopup();
      } );
    }

    // Submit button
    if ( submitBtn ) {
      submitBtn.addEventListener( 'click', () => this.handlePopupSubmit() );
    }

    // Enter key
    if ( input ) {
      input.addEventListener( 'keypress', ( e ) => {
        if ( e.key === 'Enter' ) {
          this.handlePopupSubmit();
        }
      } );

      // Only numbers
      input.addEventListener( 'input', ( e ) => {
        e.target.value = e.target.value.replace( /[^0-9]/g, '' );
        this.hidePopupError();
      } );
    }

    // Close on overlay click
    popup.addEventListener( 'click', ( e ) => {
      if ( e.target === popup ) {
        this.hidePopup();
      }
    } );
  }

  setupTriggerButtons() {
    // Setup all trigger buttons on the page
    document.addEventListener( 'click', ( e ) => {
      if ( e.target.closest( '.pincode-trigger-btn, [data-pincode-trigger]' ) ) {
        e.preventDefault();
        this.showPopup();
      }
    } );
  }

  handlePopupSubmit() {
    const input = document.getElementById( 'globalPincodeInput' );
    const pincode = input?.value.trim();

    if ( !this.validatePincode( pincode ) ) {
      this.showPopupError( 'Please enter a valid 6-digit pincode' );
      return;
    }

    // Save pincode
    this.savePincode( pincode );

    // Show result in popup
    this.showPopupResult( pincode );

    // Update UI
    this.updateGlobalUI();
  }

  validatePincode( pincode ) {
    return /^\d{6}$/.test( pincode );
  }

  savePincode( pincode ) {
    this.currentPincode = pincode;

    localStorage.setItem( this.STORAGE_KEY, JSON.stringify( {
      pincode: pincode,
      timestamp: new Date().toISOString()
    } ) );

    sessionStorage.removeItem( this.SKIP_KEY );

    this.checkDeliveryEligibility();
  }

  showPopup() {
    const popup = document.getElementById( 'globalPincodePopup' );
    if ( popup ) {
      popup.style.display = 'flex';
      document.body.style.overflow = 'hidden';

      // Focus input
      setTimeout( () => {
        const input = document.getElementById( 'globalPincodeInput' );
        if ( input ) input.focus();
      }, 100 );
    }
  }

  hidePopup() {
    const popup = document.getElementById( 'globalPincodePopup' );
    if ( popup ) {
      popup.style.display = 'none';
      document.body.style.overflow = '';
    }
  }

  showPopupError( message ) {
    const error = document.getElementById( 'globalPincodeError' );
    if ( error ) {
      error.textContent = message;
      error.style.display = 'block';
    }
  }

  hidePopupError() {
    const error = document.getElementById( 'globalPincodeError' );
    if ( error ) {
      error.style.display = 'none';
    }
  }

  showPopupResult( pincode ) {
    const popup = document.getElementById( 'globalPincodePopup' );
    const content = popup?.querySelector( '.global-pincode-content' );
    if ( !content ) return;

    // Check delivery eligibility
    const isPincodeEligible = this.deliveryEligibility?.isPincodeEligible;
    const isTimeEligible = this.deliveryEligibility?.isTimeEligible;
    const location = this.deliveryEligibility?.location;
    const edd = this.deliveryEligibility?.edd;
    const timeMessage = this.deliveryEligibility?.timeMessage;

    let resultHTML = '';
    let resultClass = '';
    let icon = '';
    let title = '';
    let message = '';

    if ( isPincodeEligible && isTimeEligible ) {
      // Instant delivery available
      resultClass = 'success instant';
      icon = '<svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon></svg>';
      title = this.config?.responseInstantTitle || '';
      const instantMsg = this.config?.responseInstantMessage || '';
      message = this.replacePincodePlaceholder( instantMsg, pincode );
      if ( edd ) {
        message += `<br><span class="result-edd">${ edd }</span>`;
      }
      if ( location ) {
        message += `<span class="result-location">${ location }</span>`;
      }
    } else if ( isPincodeEligible && !isTimeEligible ) {
      // Pincode eligible but time restricted
      resultClass = 'warning time-restricted';
      icon = '<svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>';
      title = this.config?.responseTimeRestrictedTitle || '';
      const restrictedMsg = this.config?.responseTimeRestrictedMessage || '';
      message = this.replacePincodePlaceholder( restrictedMsg, pincode );
      // if ( edd ) {
      //   message += `<br><span class="result-edd">${ edd }</span>`;
      // }
    } else {
      // Out of area
      resultClass = 'info out-of-area';
      icon = '<svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="1" y="3" width="15" height="13"></rect><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon><circle cx="5.5" cy="18.5" r="2.5"></circle><circle cx="18.5" cy="18.5" r="2.5"></circle></svg>';
      title = this.config?.responseOutOfAreaTitle || '';
      const outOfAreaMsg = this.config?.responseOutOfAreaMessage || '';
      message = this.replacePincodePlaceholder( outOfAreaMsg, pincode );
    }

    resultHTML = `
      <div class="global-pincode-icon ${ resultClass }">
        ${ icon }
      </div>
      <h2 class="global-pincode-title">${ title }</h2>
      <div class="global-pincode-result-message ${ resultClass }">
        ${ message }
    
      </div>
      <div class="global-pincode-actions">
        <button type="button" class="global-pincode-primary-btn" onclick="window.globalPincodeManager.hidePopup()">${ this.config?.responseContinueButton || 'Continue Shopping' }</button>
        <button type="button" class="global-pincode-secondary-btn" onclick="window.globalPincodeManager.resetPopup()">${ this.config?.responseChangeButton || 'Change Pincode' }</button>
      </div>
    `;

    content.innerHTML = resultHTML;
  }

  resetPopup() {
    const popup = document.getElementById( 'globalPincodePopup' );
    const content = popup?.querySelector( '.global-pincode-content' );
    if ( !content ) return;

    const input = document.getElementById( 'globalPincodeInput' );
    const currentPincode = this.currentPincode || '';

    content.innerHTML = `
      <div class="global-pincode-icon">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
          <circle cx="12" cy="10" r="3"></circle>
        </svg>
      </div>
      
      <h2 class="global-pincode-title">${ this.config?.popupTitle || 'Check Instant Delivery' }</h2>
      <p class="global-pincode-description">${ this.config?.popupDescription || 'Enter your pincode to check if instant delivery is available in your area' }</p>
      
      <div class="global-pincode-input-wrapper">
        <input 
          type="text" 
          id="globalPincodeInput"
          class="global-pincode-input" 
          placeholder="Enter 6-digit pincode"
          maxlength="6"
          pattern="[0-9]{6}"
          value="${ currentPincode }"
        />
        <button type="button" id="globalPincodeSubmit" class="global-pincode-submit-btn">
          ${ this.config?.popupButtonText || 'Check Availability' }
        </button>
      </div>
      
      <div class="global-pincode-error" id="globalPincodeError" style="display: none;"></div>
      
      <button type="button" id="skipPincodeCheck" class="global-pincode-skip-btn">
        ${ this.config?.popupSkipText || 'Skip for now' }
      </button>
    `;

    // Re-attach event listeners
    this.setupPopupListeners();

    // Focus input
    setTimeout( () => {
      const newInput = document.getElementById( 'globalPincodeInput' );
      if ( newInput ) newInput.focus();
    }, 100 );
  }

  updateGlobalUI() {
    // Update all product delivery messages
    this.updateProductMessages();

    // Update collection badges
    this.updateCollectionBadges();

    // Update announcement bar
    this.updateAnnouncementBar();
  }

  updateProductMessages() {
    const messages = document.querySelectorAll( '[data-product-delivery-message]' );

    messages.forEach( message => {
      const productId = message.dataset.productId;
      const instantDeliveryEnabled = message.dataset.instantDelivery === 'true';
      const productAvailable = message.dataset.productAvailable === 'true';

      if ( !productAvailable ) {
        message.innerHTML = this.getOutOfStockMessage();
        return;
      }

      if ( !this.currentPincode ) {
        message.innerHTML = this.getNoPincodeMessage();
        return;
      }

      if ( !this.deliveryEligibility ) {
        message.innerHTML = this.getStandardDeliveryMessage();
        return;
      }

      const canShowInstant = instantDeliveryEnabled &&
        this.deliveryEligibility.isPincodeEligible &&
        this.deliveryEligibility.isTimeEligible;

      if ( canShowInstant ) {
        message.innerHTML = this.getInstantDeliveryMessage();
      } else if ( instantDeliveryEnabled && this.deliveryEligibility.isPincodeEligible && !this.deliveryEligibility.isTimeEligible ) {
        message.innerHTML = this.getTimeRestrictedMessage();
      } else {
        message.innerHTML = this.getStandardDeliveryMessage();
      }
    } );
  }

  updateCollectionBadges() {
    // Only process badges with 'instant-delivery-eligible' class for better performance
    // This class is added by Liquid when product metafield is true
    const eligibleBadges = document.querySelectorAll( '.instant-delivery-eligible[data-instant-delivery-badge]' );

    if ( eligibleBadges.length === 0 ) {
      return;
    }

    eligibleBadges.forEach( badge => {
      // Product is already eligible (metafield checked in Liquid)
      // Now only check pincode and time eligibility
      const canShow = this.deliveryEligibility?.isPincodeEligible &&
        this.deliveryEligibility?.isTimeEligible;

      if ( canShow ) {
        // Update EDD text for text badges
        const badgeType = badge.dataset.badgeType;
        if ( badgeType === 'text' ) {
          const eddElement = badge.querySelector( '[data-edd-text]' );
          if ( eddElement && this.deliveryEligibility?.edd ) {
            eddElement.textContent = this.deliveryEligibility.edd;
          }
        }

        badge.classList.add( 'badge-visible' );
      } else {
        badge.classList.remove( 'badge-visible' );
      }
    } );
  }

  getNoPincodeMessage() {
    return `
      <div class="product-delivery-message no-pincode">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
          <circle cx="12" cy="10" r="3"></circle>
        </svg>
        <div class="delivery-message-text">
          <strong>${ this.config?.popupTitle }</strong>
          <button class="pincode-trigger-btn">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
              <circle cx="12" cy="10" r="3"></circle>
            </svg>
            ${ this.config?.responseChangeButton }
          </button>
        </div>
      </div>
    `;
  }

  getInstantDeliveryMessage() {
    return `
      <div class="product-delivery-message instant">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
        </svg>
        <div class="delivery-message-text">
          <div>
            <strong>${ this.config?.responseInstantTitle }</strong>
            <span class="delivery-edd">${ this.deliveryEligibility?.edd || 'Fast delivery' } • ${ this.currentPincode }</span>
          </div>
          <button class="pincode-trigger-btn">
            ${ this.config?.responseChangeButton }
          </button>
        </div>
      </div>
    `;
  }

  getStandardDeliveryMessage() {
    return `
      <div class="product-delivery-message standard">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <rect x="1" y="3" width="15" height="13"></rect>
          <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon>
          <circle cx="5.5" cy="18.5" r="2.5"></circle>
          <circle cx="18.5" cy="18.5" r="2.5"></circle>
        </svg>
        <div class="delivery-message-text">
          <div>
            <strong>${ this.config?.responseOutOfAreaTitle }</strong>
            <span class="delivery-edd">Pincode: ${ this.currentPincode }</span>
          </div>
          <button class="pincode-trigger-btn">
            ${ this.config?.responseChangeButton }
          </button>
        </div>
      </div>
    `;
  }

  getTimeRestrictedMessage() {
    return `
      <div class="product-delivery-message standard">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10"></circle>
          <polyline points="12 6 12 12 16 14"></polyline>
        </svg>
        <div class="delivery-message-text">
        <div>
          <strong>${ this.config?.responseTimeRestrictedTitle }</strong>
          <span class="delivery-edd">
            ${ this.deliveryEligibility?.edd || 'Standard delivery available' }
            • ${ this.currentPincode }
          </span>
        </div>
        <button class="pincode-trigger-btn">
          ${ this.config?.responseChangeButton }
        </button>
      </div>
      </div>
    `;
  }

  getOutOfStockMessage() {
    return `
      <div class="product-delivery-message" style="background: #ffebee; border-color: #ef9a9a; color: #c62828;">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
        <div class="delivery-message-text">
          <strong>Out of Stock</strong>
        </div>
      </div>
    `;
  }

  updateAnnouncementBar() {
    const announcementBar = document.querySelector( '[data-announcement-bar-pincode]' );
    if ( !announcementBar ) return;

    if ( !this.currentPincode ) {
      // No pincode - show prompt to enter
      announcementBar.innerHTML = `
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
          <circle cx="12" cy="10" r="3"></circle>
        </svg>
        <span class="announcement-bar-pincode-text">${ this.config?.popupTitle }</span>
      `;
      announcementBar.onclick = () => this.showPopup();
      return;
    }

    // Has pincode - show delivery status
    const isPincodeEligible = this.deliveryEligibility?.isPincodeEligible;
    const isTimeEligible = this.deliveryEligibility?.isTimeEligible;
    const edd = this.deliveryEligibility?.edd;
    const timeMessage = this.deliveryEligibility?.timeMessage;

    let icon = '';
    let text = '';

    if ( isPincodeEligible && isTimeEligible ) {
      // Instant delivery available
      icon = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon></svg>';
      text = `${ this.config?.responseInstantTitle } <span class="announcement-bar-pincode-value">${ this.currentPincode }</span> • ${ edd || 'Fast delivery' }`;
    } else if ( isPincodeEligible && !isTimeEligible ) {
      // Time restricted
      icon = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>';
      text = `${ this.config?.responseTimeRestrictedTitle } <span class="announcement-bar-pincode-value">${ this.currentPincode }</span> • ${ timeMessage || 'Standard delivery available' }`;
    } else {
      // Standard delivery
      icon = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="1" y="3" width="15" height="13"></rect><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon><circle cx="5.5" cy="18.5" r="2.5"></circle><circle cx="18.5" cy="18.5" r="2.5"></circle></svg>';
      text = `${ this.config?.responseOutOfAreaTitle }: <span class="announcement-bar-pincode-value">${ this.currentPincode }</span>`;
    }

    announcementBar.innerHTML = `
      ${ icon }
      <span class="announcement-bar-pincode-text">
        ${ text }
        <span class="announcement-bar-pincode-change">${ this.config?.responseContinueButton }</span>
      </span>
    `;
    announcementBar.onclick = () => this.showPopup();
  }

  // Public API
  getPincode() {
    return this.currentPincode;
  }

  getDeliveryEligibility() {
    return this.deliveryEligibility;
  }

  clearPincode() {
    this.currentPincode = null;
    this.deliveryEligibility = null;
    localStorage.removeItem( this.STORAGE_KEY );
    sessionStorage.removeItem( this.SKIP_KEY );
    sessionStorage.removeItem( this.SKIP_TIMESTAMP_KEY );
    if ( this.popupTimer ) {
      clearTimeout( this.popupTimer );
      this.popupTimer = null;
    }
    this.updateGlobalUI();
  }
}

// Initialize global instance
window.globalPincodeManager = new GlobalPincodeManager();
console.log( window.globalPincodeManager.config );
